/**
 * Envelope and bill balances. Ported from `BudgetStore.swift`.
 *
 * Balances are allocation-driven: an envelope's spend is the sum of the
 * allocations pointing at it, not a running counter. That makes recategorizing
 * a transaction a single write with no balance to repair.
 *
 * ## One deliberate divergence from iOS
 *
 * `BudgetStore.spentAmount(for:)` sums every active allocation ever made, with
 * no period filter, while `EditEnvelopeCategoryView` labels that number "Spent
 * This Period". Both cannot be right. Pass a `period` to get the scoped answer
 * the label promises; omit it to reproduce the iOS number exactly.
 */

import { roundCents } from './money';
import {
  monthContaining,
  PaycheckPeriod,
  periodContains,
  previousPeriod,
} from './paycheck';
import { transactionFlow, TransactionFlow } from './transaction-display';
import { Allocation, BudgetItem, Transaction } from './types';

export function transactionFlowOf(transaction: Transaction): TransactionFlow {
  return transactionFlow(transaction.type, transaction.description, transaction.amount);
}

/**
 * Precomputed lookups. Every balance question needs "which transaction does
 * this allocation belong to", and doing that with `Array.find` per allocation
 * is quadratic — noticeable at a few thousand transactions.
 */
export interface BudgetIndex {
  transactionsById: Map<string, Transaction>;
  allocationsByBudgetItem: Map<string, Allocation[]>;
  allocationsByTransaction: Map<string, Allocation[]>;
}

/**
 * Trashed transactions are excluded here rather than at every call site, so a
 * trashed charge stops counting against its envelope the moment it is trashed.
 */
export function buildIndex(
  transactions: readonly Transaction[],
  allocations: readonly Allocation[]
): BudgetIndex {
  const transactionsById = new Map<string, Transaction>();
  for (const transaction of transactions) {
    if (transaction.deletedAt == null) {
      transactionsById.set(transaction.id, transaction);
    }
  }

  const allocationsByBudgetItem = new Map<string, Allocation[]>();
  const allocationsByTransaction = new Map<string, Allocation[]>();
  for (const allocation of allocations) {
    if (!transactionsById.has(allocation.transactionId)) continue;

    const forItem = allocationsByBudgetItem.get(allocation.budgetItemId);
    if (forItem) forItem.push(allocation);
    else allocationsByBudgetItem.set(allocation.budgetItemId, [allocation]);

    const forTransaction = allocationsByTransaction.get(allocation.transactionId);
    if (forTransaction) forTransaction.push(allocation);
    else allocationsByTransaction.set(allocation.transactionId, [allocation]);
  }

  return { transactionsById, allocationsByBudgetItem, allocationsByTransaction };
}

/**
 * Total spent against a budget item.
 *
 * Refunds subtract instead of adding: a positive transaction amount means money
 * came back, so returning a shirt should free up the clothing envelope rather
 * than double-charging it.
 */
export function spentAmount(
  budgetItemId: string,
  index: BudgetIndex,
  period?: PaycheckPeriod
): number {
  const allocations = index.allocationsByBudgetItem.get(budgetItemId);
  if (!allocations) return 0;

  let total = 0;
  for (const allocation of allocations) {
    const transaction = index.transactionsById.get(allocation.transactionId);
    if (!transaction) continue;
    if (period && !periodContains(period, transaction.transactionDate)) continue;
    const isRefund = transaction.amount > 0;
    total += isRefund ? -Math.abs(allocation.amount) : Math.abs(allocation.amount);
  }
  return roundCents(total);
}

/**
 * What a fund brings forward from the period before.
 *
 * A fund is meant to build up for something later, so money it did not spend
 * last period is still its money. A plain envelope starts fresh every paycheck.
 *
 * Memory is one period deep, not all of history. Reaching further back would
 * need a record of when the item started being funded, and the only date stored
 * is `createdAt` — which for this data is the day the rows were seeded, so
 * multiplying by paychecks-since would invent balances nobody saved.
 *
 * Overspending carries too, as a negative: spend $50 past a fund last period and
 * this period starts $50 down. Hiding that would make the overspend disappear.
 *
 * `rolloverKickoffDate` is a fresh-books line: if the previous funding window
 * started before that day, carry is $0 so an empty prior period cannot invent
 * leftover after a restart.
 */
export function fundCarryIn(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): number {
  // Without a period there is nothing to carry from: the caller is asking about
  // all of history at once, which already includes last period's spending.
  if (!item.isFund || !period) return 0;
  const window = fundingWindow(item, period);
  const previous = previousPeriod(window);
  if (rolloverKickoffDate && previous.start < rolloverKickoffDate) return 0;
  return roundCents(item.perPaycheckAmount - spentAgainst(item, index, previous, items));
}

/**
 * The stretch of time an item's funding has to cover.
 *
 * An item funded by both paychecks is refilled every period, so a period is the
 * right window. One funded once a month gets its whole cost in a single go and
 * has to make it last — measuring it by the paycheck would credit the money
 * twice over and call it untouched in the half that paid nothing into it.
 */
export function fundingWindow(item: BudgetItem, period: PaycheckPeriod): PaycheckPeriod {
  const schedule = item.fundingSchedule ?? 'everyPaycheck';
  return schedule === 'everyPaycheck' ? period : monthContaining(period);
}

/** Own spend plus one level of children, in an already-chosen date window. */
export function spentAgainst(
  item: BudgetItem,
  index: BudgetIndex,
  period: PaycheckPeriod | undefined,
  items: readonly BudgetItem[] = []
): number {
  const own = spentAmount(item.id, index, period);
  const childSpend = items
    .filter((candidate) => candidate.parentId === item.id)
    .reduce((total, child) => total + spentAmount(child.id, index, period), 0);
  return roundCents(own + childSpend);
}

/** What has been spent against an item over the stretch its funding covers. */
export function spentInWindow(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  items: readonly BudgetItem[] = []
): number {
  return spentAgainst(item, index, period && fundingWindow(item, period), items);
}

/** A child with no funding of its own is a tracking bucket, not an overspend. */
export function isTrackingChild(item: BudgetItem): boolean {
  return Boolean(item.parentId) && !(item.perPaycheckAmount > 0);
}

export function itemPathLabel(item: BudgetItem, items: readonly BudgetItem[]): string {
  const parent = item.parentId
    ? items.find((candidate) => candidate.id === item.parentId)
    : undefined;
  return parent ? `${parent.name} › ${item.name}` : item.name;
}

/** What can be spent this period: one round of funding plus a fund's carry-in. */
export function availableAmount(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): number {
  return roundCents(
    item.perPaycheckAmount + fundCarryIn(item, index, period, rolloverKickoffDate, items)
  );
}

export function remainingBalance(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): number {
  return roundCents(
    availableAmount(item, index, period, rolloverKickoffDate, items) -
      spentInWindow(item, index, period, items)
  );
}

export function isOverBudget(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): boolean {
  if (isTrackingChild(item)) return false;
  return (
    spentInWindow(item, index, period, items) >
    availableAmount(item, index, period, rolloverKickoffDate, items)
  );
}

/** Under a quarter left, but not yet empty. */
export function isLowBalance(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): boolean {
  if (isTrackingChild(item)) return false;
  const remaining = remainingBalance(item, index, period, rolloverKickoffDate, items);
  return (
    remaining > 0 &&
    remaining < availableAmount(item, index, period, rolloverKickoffDate, items) * 0.25
  );
}

export interface BudgetItemBalance {
  item: BudgetItem;
  /**
   * What one round of funding gives this item — `perPaycheckAmount`, never more.
   *
   * Deliberately unaffected by a fund's carry-in, because summing this across
   * items is how the app answers "what do the envelopes add up to per paycheck",
   * which the paycheck plan is checked against. Use `available` for "how much can
   * be spent right now". For an item funded once a month, one round is the whole
   * month's cost, and it only lands on the paycheck that funds it.
   */
  budgeted: number;
  /** Left over from last period, for a fund. Zero for a plain envelope or bill. */
  carriedIn: number;
  /** `budgeted + carriedIn`: what this period can actually spend. */
  available: number;
  spent: number;
  remaining: number;
  /** 0–1, clamped, for progress bars. Use `isOverBudget` for the overspend case. */
  progress: number;
  isOverBudget: boolean;
  isLowBalance: boolean;
  isEmpty: boolean;
  transactionCount: number;
}

export function budgetItemBalance(
  item: BudgetItem,
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null,
  items: readonly BudgetItem[] = []
): BudgetItemBalance {
  const window = period && fundingWindow(item, period);
  const spent = spentAgainst(item, index, window, items);
  const carriedIn = fundCarryIn(item, index, period, rolloverKickoffDate, items);
  const available = roundCents(item.perPaycheckAmount + carriedIn);
  const remaining = roundCents(available - spent);
  const tracking = isTrackingChild(item);
  const allocations = index.allocationsByBudgetItem.get(item.id) ?? [];
  const counted = window
    ? allocations.filter((allocation) => {
        const transaction = index.transactionsById.get(allocation.transactionId);
        return transaction != null && periodContains(window, transaction.transactionDate);
      })
    : allocations;

  return {
    item,
    budgeted: item.perPaycheckAmount,
    carriedIn,
    available,
    spent,
    remaining,
    progress: available > 0 ? Math.min(Math.max(spent / available, 0), 1) : 0,
    isOverBudget: tracking ? false : spent > available,
    isLowBalance: tracking ? false : remaining > 0 && remaining < available * 0.25,
    isEmpty: tracking ? false : remaining <= 0,
    transactionCount: new Set(counted.map((allocation) => allocation.transactionId)).size,
  };
}

export function budgetItemBalances(
  items: readonly BudgetItem[],
  index: BudgetIndex,
  period?: PaycheckPeriod,
  rolloverKickoffDate?: string | null
): BudgetItemBalance[] {
  return items.map((item) => budgetItemBalance(item, index, period, rolloverKickoffDate, items));
}

/**
 * Money actually spent during a paycheck period.
 *
 * Only spending counts. Moving money to savings or a deposit landing is your
 * money changing pockets, not money gone, and counting a transfer would make
 * payday look like a spree. Refunds come back off the total. Pending holds do
 * count: the money is committed even though it has not settled.
 */
export function moneySpent(
  transactions: readonly Transaction[],
  period: PaycheckPeriod
): number {
  let total = 0;
  for (const transaction of transactions) {
    if (transaction.deletedAt != null) continue;
    if (!periodContains(period, transaction.transactionDate)) continue;

    switch (transactionFlowOf(transaction)) {
      case 'spending':
      case 'sent':
      case 'withdrawal':
        if (transaction.amount < 0) total += Math.abs(transaction.amount);
        break;
      case 'refund':
        total -= Math.abs(transaction.amount);
        break;
      case 'deposit':
      case 'transferIn':
      case 'transferOut':
        break;
    }
  }
  return roundCents(total);
}

/** What is left of the paycheck that funds this period. */
export function leftToSpend(
  transactions: readonly Transaction[],
  period: PaycheckPeriod,
  paycheckAmount: number
): number {
  return roundCents(paycheckAmount - moneySpent(transactions, period));
}

export function isCategorized(transactionId: string, index: BudgetIndex): boolean {
  return (index.allocationsByTransaction.get(transactionId)?.length ?? 0) > 0;
}

export function isSplit(transactionId: string, index: BudgetIndex): boolean {
  return (index.allocationsByTransaction.get(transactionId)?.length ?? 0) > 1;
}

export function allocationsFor(transactionId: string, index: BudgetIndex): Allocation[] {
  return index.allocationsByTransaction.get(transactionId) ?? [];
}

/**
 * Charges still waiting to be sorted. Refunds are excluded on purpose: where a
 * refund lands is a judgement call, so it should not nag from the inbox count.
 */
export function uncategorizedTransactions(
  transactions: readonly Transaction[],
  index: BudgetIndex
): Transaction[] {
  return transactions.filter(
    (transaction) =>
      transaction.deletedAt == null &&
      transaction.status !== 'joined' &&
      !isCategorized(transaction.id, index)
  );
}